// ONYX-CUSTOM: cart drawer upsell. Fills the desktop second drawer level, completes the list with Shopify recommendations when needed and keeps both in step with the cart.
//
// Loaded with type="module" from sections/cart-drawer.liquid. The list itself is rendered by
// snippets/onyx-cart-upsell.liquid inside the drawer body, which the theme swaps on every cart
// change. That element is the single source of truth: the panel only shows a copy of its cards.

const VENDOR_TAG = 'cart-drawer-products-recommendation';

// .drawer__inner slides for 0.6s (assets/theme.css); the panel starts sliding out a beat after that.
// A static timer from the drawer's `active` attribute: recommendations only delay it when they are slower.
const DRAWER_TRANSITION = 600;
const REVEAL_DELAY = 150;

const desktop = window.matchMedia('(min-width: 1024px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// Recommendations are the only part of the list that costs a request, so the answer for a given
// cart state is kept. A quantity change re-renders the plain list; this puts the completed one back.
const completed = new Map();
let pending = null;

const getPanel = () => document.querySelector('onyx-cart-upsell-panel');
const getDrawer = () => document.querySelector('cart-drawer');

const whenDrawerOpen = () =>
  new Promise((resolve) => {
    const drawer = getDrawer();
    if (!drawer || drawer.hasAttribute('open')) {
      resolve();
      return;
    }
    const observer = new MutationObserver(() => {
      if (!drawer.hasAttribute('open')) return;
      observer.disconnect();
      resolve();
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
  });

class OnyxCartUpsellPanel extends HTMLElement {
  connectedCallback() {
    this.drawer = this.closest('cart-drawer');
    this.list = this.querySelector('[data-onyx-cart-upsell-list]');
    this.hasCards = false;
    this.ready = true;
    this.settled = false;
    this.isOpen = false;
    this.header = this.drawer.querySelector('.drawer__header');

    this.update = this.update.bind(this);
    this.observer = new MutationObserver(() => this.onDrawerChange());
    this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['active'] });
    desktop.addEventListener('change', this.update);

    // The first card lines up with the divider under the cart header, wherever that ends up.
    this.measure = this.measure.bind(this);
    this.resizeObserver = new ResizeObserver(this.measure);
    if (this.header) this.resizeObserver.observe(this.header);
    this.measure();

    this.onDrawerChange();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    desktop.removeEventListener('change', this.update);
    clearTimeout(this.timer);
  }

  measure() {
    // Not rendered while the drawer is hidden; the observer measures again when it appears.
    if (!this.header?.offsetHeight) return;
    const offset = this.header.getBoundingClientRect().bottom - this.drawer.getBoundingClientRect().top;
    this.style.setProperty('--onyx-cart-upsell-offset', `${offset}px`);
  }

  setLoading(loading) {
    this.setAttribute('aria-busy', String(loading));
  }

  // An unfinished list never replaces one that is already on screen: the old cards stay, dimmed,
  // until the completed list arrives, so the panel does not close and open on every cart change.
  setCards(html, ready) {
    if (!ready && this.isOpen) {
      this.setLoading(true);
      return;
    }

    const template = document.createElement('template');
    template.innerHTML = html;
    const cards = [...template.content.querySelectorAll('.onyx-cart-upsell-card')];
    this.list.replaceChildren(...cards);
    this.hasCards = cards.length > 0;
    this.ready = ready;
    this.setLoading(!ready);
    this.update();
  }

  setReady(ready) {
    this.ready = ready;
    this.setLoading(!ready);
    this.update();
  }

  onDrawerChange() {
    clearTimeout(this.timer);
    this.settled = false;

    if (this.drawer.hasAttribute('active')) {
      const delay = reducedMotion.matches ? 0 : DRAWER_TRANSITION + REVEAL_DELAY;
      this.timer = setTimeout(() => {
        this.settled = true;
        this.update();
      }, delay);
    }

    this.update();
  }

  update() {
    this.hidden = !this.hasCards;

    const open = this.hasCards && (this.ready || this.isOpen) && this.settled && desktop.matches;
    if (open === this.isOpen) return;
    this.isOpen = open;

    // Two frames, so a panel that was hidden a moment ago still gets to transition.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.toggleAttribute('data-onyx-open', this.isOpen);
        this.inert = !this.isOpen;
        if (this.isOpen) this.trapFocus();
      })
    );
  }

  // The theme traps focus on #CartDrawer-<id> after every update, which leaves this panel out.
  trapFocus() {
    if (!this.isOpen || !this.drawer.hasAttribute('open')) return;
    const active = document.activeElement;
    FoxTheme.a11y.trapFocus(this.drawer, this.drawer.contains(active) ? active : this.drawer);
  }
}
customElements.define('onyx-cart-upsell-panel', OnyxCartUpsellPanel);

const load = async (block) => {
  pending?.abort();
  const request = new AbortController();
  pending = request;

  await whenDrawerOpen();
  if (request.signal.aborted || !block.isConnected) return;

  try {
    const response = await fetch(block.dataset.url, { signal: request.signal });
    if (!response.ok) throw new Error(`Recommendations request failed with ${response.status}`);

    const html = await response.text();
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('onyx-cart-upsell');
    if (!next) throw new Error('No upsell list in the recommendations response');

    // The theme re-renders the plain list on the next cart change; this puts the completed one back.
    next.dataset.needsApi = 'false';
    completed.set(block.dataset.state, next.outerHTML);
    if (block.isConnected) block.replaceWith(next);
  } catch (error) {
    if (error.name === 'AbortError') return;
    console.error('Cart upsell recommendations:', error);
    getPanel()?.setReady(true);
  }
};

// Joins the theme's slider element for the carousel and mobile behaviour, but not for its data:
// the vendor class fetches its own recommendations in init(), which the server render replaces.
await customElements.whenDefined(VENDOR_TAG);

class OnyxCartUpsell extends customElements.get(VENDOR_TAG) {
  init() {}

  connectedCallback() {
    const needsApi = this.dataset.needsApi === 'true';
    const restored = needsApi ? completed.get(this.dataset.state) : null;
    if (restored) {
      this.outerHTML = restored;
      return;
    }

    // Before the carousel runs: Swiper rewrites the slides, the panel wants them as rendered.
    const cards = this.querySelector('[data-onyx-cart-upsell-cards]')?.innerHTML ?? '';
    this.handleCarousel();
    getPanel()?.setCards(cards, !needsApi);

    if (needsApi) load(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    // A cart update replaces this element within the same task; an emptied cart does not.
    queueMicrotask(() => {
      if (!document.querySelector('onyx-cart-upsell')) getPanel()?.setCards('', true);
    });
  }
}
customElements.define('onyx-cart-upsell', OnyxCartUpsell);

// Runs after the theme has swapped the drawer body and set its own focus trap.
document.addEventListener('cart:updated', () => getPanel()?.trapFocus());
